import { Stack } from '@mui/joy';
import { useParams } from '@tanstack/react-router';

import CopyIcon from '@/assets/icons/copy.svg?react';
import { ButtonLink } from '@/components/router/ButtonLink';
import { EditModalDialog } from '@/components/signs/modal/EditModalDialog';
import { useSignGroupQuery } from '@/queries/useSignGroupQuery';
import { useCreateSignStore } from '@/stores/useCreateSignStore';
import { useMapStore } from '@/stores/useMapStore';
import { useSignsTableStore } from '@/stores/useSignsTableStore';
import { LocationInputMode } from '@/types/LocationInputMode';
import { SignsTableMode } from '@/types/SignsTableState';

type DuplicateModalProps = { setOpen: (open: boolean) => void };

export function DuplicateModal({ setOpen }: DuplicateModalProps) {
  const { adminLevelId, signSupportId, signGroupId } = useParams({ strict: false });
  const { data: group } = useSignGroupQuery();

  const { setLocationInputMode, resetStore } = useCreateSignStore((state) => state.actions);
  const { setMode } = useSignsTableStore((state) => state.actions);
  const { setSnackbar } = useMapStore((state) => state.actions);

  if (!adminLevelId || !signSupportId || !signGroupId || !group) {
    return null;
  }

  const abort = () => {
    setOpen(false);
    setLocationInputMode(LocationInputMode.DEFAULT);
  };

  return (
    <EditModalDialog onClose={abort} title="Verkehrszeichen-Gruppe duplizieren">
      <Stack gap={1}>
        <ButtonLink
          variant="outlined"
          to="/verkehrszeichen/$adminLevelId/$signSupportId/$signGroupId"
          params={{ adminLevelId, signSupportId, signGroupId }}
          onClick={() => {
            resetStore();
            setSnackbar({
              color: 'neutral',
              startDecorator: <CopyIcon />,
              text: 'Verkehrszeichen duplizieren. Aufstellvorrichtung erstellen',
              onClose: () => {
                setSnackbar(undefined);
                abort();
              },
            });
            setMode(SignsTableMode.MAP);
            setLocationInputMode(LocationInputMode.DUPLICATE);
            setOpen(false);
          }}
        >
          Aufstellvorrichtung erstellen
        </ButtonLink>

        <ButtonLink
          variant="outlined"
          to="/verkehrszeichen/$adminLevelId/$signSupportId/$signGroupId"
          params={{ adminLevelId, signSupportId, signGroupId }}
          onClick={() => {
            resetStore();
            setSnackbar({
              color: 'neutral',
              startDecorator: <CopyIcon />,
              text: 'Verkehrszeichen duplizieren. Aufstellvorrichtung auswählen',
              onClose: () => {
                setSnackbar(undefined);
                abort();
              },
            });
            setMode(SignsTableMode.MAP);
            setLocationInputMode(LocationInputMode.SELECT);
            setOpen(false);
          }}
        >
          Aufstellvorrichtung auswählen
        </ButtonLink>
      </Stack>
    </EditModalDialog>
  );
}
